(function () {
  'use strict';

  // Reason: match on path fragments so locale prefixes (e.g. /en-nz/...) still work.
  // Both the "Request a Quote" and "Request Visuals" Shopify Forms get prefilled
  // from the same URL params (product / quantity / notes). The Visuals form has
  // no quantity field, which is fine: missing fields are skipped gracefully.
  var QUOTE_PATH_FRAGMENT = '/pages/request-a-quote';
  var PREFILL_PATHS = ['/pages/request-a-quote', '/pages/request-visuals'];
  var QUOTE_LINK_SELECTOR = '[data-request-quote-link]';

  // Flip to false once prefill is confirmed working in production.
  var DEBUG = true;

  // The Shopify Forms app renders the form inside the OPEN shadow root of a
  // <shopify-forms-embed> custom element. Fields are NOT in the light DOM and
  // there is no <form-embed> element. Custom fields get ids of the form
  // `custom#<field-key>`, e.g. custom#product / custom#quantity / custom#notes.
  var EMBED_SELECTORS = ['shopify-forms-embed', 'form-embed'];

  // Each URL param maps to a list of selectors (tried in order) plus keyword
  // fallbacks (matched against id/name/placeholder/label) for resilience.
  var FIELD_CONFIG = {
    product: {
      param: 'product',
      selectors: ['[id="custom#product"]', '[data-testid="field-custom#product"]'],
      keywords: ['product', 'item'],
      tags: 'input, textarea',
    },
    quantity: {
      param: 'quantity',
      selectors: ['[id="custom#quantity"]', '[data-testid="field-custom#quantity"]'],
      keywords: ['quantity', 'qty'],
      tags: 'input',
    },
    notes: {
      param: 'notes',
      selectors: ['[id="custom#notes"]', '[name="custom#notes"]'],
      keywords: ['notes', 'message', 'details'],
      tags: 'textarea, input',
    },
  };

  // Poll/retry config. Shopify Forms hydrates asynchronously and may re-render,
  // so we keep checking until fields exist and re-apply values a few times.
  var POLL_INTERVAL_MS = 400;
  var MAX_ATTEMPTS = 60; // ~24s waiting for the embed/fields to appear
  var MAX_REINFORCEMENTS = 4; // re-apply after first success to beat hydration resets

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[QuotePrefill]');
    // eslint-disable-next-line no-console
    console.log.apply(console, args);
  }

  // React tracks input/textarea values via its own setter, so a plain
  // `el.value = x` can be ignored on re-render. Use the native prototype setter
  // and then dispatch input/change so React picks up the change.
  function setNativeValue(el, value) {
    var proto =
      typeof HTMLTextAreaElement !== 'undefined' && el instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype;
    var descriptor = Object.getOwnPropertyDescriptor(proto, 'value');
    if (descriptor && descriptor.set) {
      descriptor.set.call(el, value);
    } else {
      el.value = value;
    }
    el.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
    el.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
  }

  function getEmbedShadowRoot() {
    for (var i = 0; i < EMBED_SELECTORS.length; i += 1) {
      var embed = document.querySelector(EMBED_SELECTORS[i]);
      if (embed && embed.shadowRoot) return embed.shadowRoot;
    }
    return null;
  }

  function fieldMetadata(root, field) {
    var labelText = '';
    if (field.id) {
      var label = root.querySelector('label[for="' + field.id + '"]');
      labelText = label ? label.textContent || '' : '';
    }
    return [
      field.name || '',
      field.id || '',
      field.placeholder || '',
      field.getAttribute('aria-label') || '',
      labelText,
    ]
      .join(' ')
      .toLowerCase();
  }

  function findField(root, config) {
    for (var i = 0; i < config.selectors.length; i += 1) {
      var direct = root.querySelector(config.selectors[i]);
      if (direct) return direct;
    }

    // Keyword fallback (the field id usually still contains the keyword).
    var candidates = root.querySelectorAll(config.tags || 'input, textarea, select');
    for (var j = 0; j < candidates.length; j += 1) {
      var meta = fieldMetadata(root, candidates[j]);
      for (var k = 0; k < config.keywords.length; k += 1) {
        if (meta.indexOf(config.keywords[k]) !== -1) return candidates[j];
      }
    }
    return null;
  }

  function applyValue(root, config, value) {
    if (!value) return; // no param value for this field
    var field = findField(root, config);
    if (!field) {
      // Not every form has every field (e.g. Visuals has no quantity). Skip it.
      log('field not present, skipping:', config.param);
      return;
    }
    if (field.value !== value) {
      setNativeValue(field, value);
      log('set', config.param, '->', value);
    }
  }

  function getParams() {
    var params = new URLSearchParams(window.location.search);
    return {
      product: params.get('product') || '',
      quantity: params.get('quantity') || '',
      notes: params.get('notes') || '',
    };
  }

  function fillAll(root, data) {
    applyValue(root, FIELD_CONFIG.product, data.product);
    applyValue(root, FIELD_CONFIG.quantity, data.quantity);
    applyValue(root, FIELD_CONFIG.notes, data.notes);
  }

  // Dump every field so we can confirm how Shopify named them. Console:
  // QuotePrefill.inspect()
  function inspectFields() {
    var root = getEmbedShadowRoot();
    if (!root) {
      log('inspect: embed shadowRoot not available');
      return [];
    }
    var fields = root.querySelectorAll('input, textarea, select');
    var info = [];
    for (var i = 0; i < fields.length; i += 1) {
      info.push({
        tag: fields[i].tagName,
        type: fields[i].type,
        id: fields[i].id,
        name: fields[i].name,
        testid: fields[i].getAttribute('data-testid'),
        value: fields[i].value,
      });
    }
    log('inspect: found', info.length, 'fields', info);
    return info;
  }

  var pollTimer = null;

  function startPrefill() {
    var data = getParams();
    if (!data.product && !data.quantity && !data.notes) {
      log('no prefill params in URL, nothing to do.', window.location.search);
      return;
    }
    log('starting prefill with', data);

    if (pollTimer) window.clearInterval(pollTimer);

    var attempts = 0;
    var reinforcements = 0;

    function stop(reason) {
      if (pollTimer) window.clearInterval(pollTimer);
      pollTimer = null;
      log('stopped:', reason, '(attempts=' + attempts + ')');
    }

    pollTimer = window.setInterval(function () {
      attempts += 1;

      var root = getEmbedShadowRoot();
      if (!root) {
        if (attempts === 1) log('waiting for <shopify-forms-embed> shadowRoot...');
        if (attempts >= MAX_ATTEMPTS) stop('embed/shadowRoot never appeared');
        return;
      }

      var fields = root.querySelectorAll('input, textarea, select');
      if (!fields.length) {
        if (attempts >= MAX_ATTEMPTS) stop('shadowRoot present but no fields rendered');
        return;
      }

      // Form is rendered: set whatever target fields exist and re-apply a few
      // times so a late hydration pass can't blank them.
      fillAll(root, data);
      reinforcements += 1;
      if (reinforcements >= MAX_REINFORCEMENTS) {
        stop('fields filled and held');
      } else if (attempts >= MAX_ATTEMPTS) {
        stop('timed out');
      }
    }, POLL_INTERVAL_MS);
  }

  // ---- Product-page link binding (no-op unless the theme renders the link) ----

  function getQuantityFromPage(link) {
    var productContainer = link.closest('product-info') || document;
    var quantityInput = productContainer.querySelector('input[name="quantity"]');
    return quantityInput && quantityInput.value ? quantityInput.value : '1';
  }

  function buildQuoteUrl(link) {
    var url = new URL(QUOTE_PATH_FRAGMENT, window.location.origin);
    var productTitle = link.getAttribute('data-product-title') || '';
    var quantity = getQuantityFromPage(link);
    if (productTitle) url.searchParams.set('product', productTitle);
    if (quantity) url.searchParams.set('quantity', quantity);
    return url.toString();
  }

  function bindQuoteLinks() {
    var links = document.querySelectorAll(QUOTE_LINK_SELECTOR);
    if (!links.length) return;
    for (var i = 0; i < links.length; i += 1) {
      links[i].addEventListener('click', function handleQuoteClick(event) {
        event.currentTarget.setAttribute('href', buildQuoteUrl(event.currentTarget));
      });
    }
  }

  function onPrefillPage() {
    var path = window.location.pathname;
    for (var i = 0; i < PREFILL_PATHS.length; i += 1) {
      if (path.indexOf(PREFILL_PATHS[i]) !== -1) return true;
    }
    return false;
  }

  function init() {
    if (document.querySelector(QUOTE_LINK_SELECTOR)) {
      bindQuoteLinks();
    }

    if (onPrefillPage()) {
      startPrefill();
    } else {
      log('not on a prefill page, skipped. path=', window.location.pathname);
    }
  }

  // Expose a small API for manual debugging from the console.
  window.QuotePrefill = {
    run: startPrefill,
    attemptFillNow: function () {
      var root = getEmbedShadowRoot();
      if (!root) {
        log('attemptFillNow: shadowRoot not ready');
        return false;
      }
      fillAll(root, getParams());
      return true;
    },
    inspect: inspectFields,
    params: getParams,
    shadowRoot: getEmbedShadowRoot,
  };

  log('script loaded. readyState=', document.readyState);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
